(() => {
  // src/js/vehicleSearch/constants.js
  var PRODUCT_CARD_SECTION_ID = "main-product-card";
  var COLLECTION_EMPTY_STATE_SECTION_ID = "main-collection-empty-state";
  var COLLECTION_CAROUSEL_EMPTY_STATE_SECTION_ID = "main-collection-carousel-empty-state";
  var DEFAULT_PRODUCTS_PAGE_SIZE = window.Grafite?.ResearchVehicle?.defaultProductsPerPage;
  var GRAPHQL_COLLECTION_BATCH_SIZE = window.Grafite?.ResearchVehicle?.graphqlCollectionBatchSize;
  var GRAPHQL_SEARCH_BATCH_SIZE = window.Grafite?.ResearchVehicle?.graphqlSearchBatchSize;
  var OPENSEARCH_URL = window.Grafite?.ResearchVehicle?.openSearchUrl;
  var OPENSEARCH_AUTH = window.Grafite?.ResearchVehicle?.openSearchAuth;
  var MOUNT_SELECTOR = "[data-vehicle-search-mount]";
  var PRODUCT_PER_PAGE_SELECTOR = "data-products-per-page";
  var MAX_PRODUCTS_COUNT_SELECTOR = "data-max-products-count";
  var VEHICLE_SEARCH_STORAGE_KEY = window.Grafite?.ResearchVehicle?.grafiteSelectedVehicleLocalStorageKey;
  var GET_SEARCH_PRODUCTS_HANDLE_BY_SKUS = `
  query handleProductsFromSearch($query: String!, $first: Int!, $after: String, $productFilters: [ProductFilter!], $sortKey: SearchSortKeys, $reverse: Boolean) {
    search(query: $query, first: $first, after: $after, productFilters: $productFilters, sortKey: $sortKey, reverse: $reverse, types: [PRODUCT]) {
      edges {
        cursor
        node {
          ... on Product {
            handle
            productType
            variants(first: 250) {
              edges {
                node {
                  sku
                }
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
  var GET_PRODUCTS_HANDLE_BY_SKUS = `
  query handleProductsFromCollection($first: Int, $last: Int, $handle: String!, $filters: [ProductFilter!], $after: String, $before: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
    collection(handle: $handle) {
      products(first: $first, last: $last, filters: $filters, after: $after, before: $before, sortKey: $sortKey, reverse: $reverse) {
        edges {
          cursor
          node {
            handle
            productType
            variants(first: 250) {
              edges {
                node {
                  sku
                }
              }
            }
          }
        }  
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
    }
  }
`;
  var SORT_KEYS = {
    "manual": "MANUAL",
    "most-relevant": "RELEVANCE",
    "best-selling": "BEST_SELLING",
    "title-ascending": "TITLE_ASCENDING",
    "title-descending": "TITLE_DESCENDING",
    "price-ascending": "PRICE_ASCENDING",
    "price-descending": "PRICE_DESCENDING",
    "created-ascending": "CREATED_ASCENDING",
    "created-descending": "CREATED_DESCENDING",
    "collection-default": "COLLECTION_DEFAULT"
  };

  // src/js/vehicleSearch/utils.js
  function getVehicleSearchMounts() {
    return Array.from(document.querySelectorAll(MOUNT_SELECTOR));
  }
  function getProductCardsContainer(mount) {
    if (!mount) {
      return null;
    }
    if (isPredictiveSearchMount(mount)) {
      return mount.querySelector('[slot="results"] .predictive-search__products');
    }
    const carousel = mount.querySelector("scroll-carousel");
    return carousel ?? mount;
  }
  function getProductListMountHandle(mount) {
    if (!mount) {
      return null;
    }
    return mount.getAttribute("data-handle") ?? mount.getAttribute("handle") ?? null;
  }
  function getProductsPerPage(mount) {
    if (!mount) {
      return null;
    }
    return mount.getAttribute(PRODUCT_PER_PAGE_SELECTOR) ?? null;
  }
  function isProductRecommendationsMount(mount) {
    if (!mount) {
      return false;
    }
    return mount.getAttribute("data-product-recommendations") !== null;
  }
  function isPredictiveSearchMount(mount) {
    if (!mount) {
      return false;
    }
    return mount.getAttribute("data-predictive-search") !== null;
  }
  function isMainSearchMount(mount) {
    if (!mount) {
      return false;
    }
    return mount.hasAttribute("data-main-search");
  }
  function getMainSearchTerms(mount) {
    if (!mount) {
      return null;
    }
    const terms = mount.getAttribute("data-search-terms");
    return terms && terms.trim() !== "" ? terms.trim() : null;
  }
  function getProductRecommendationsProductId(mount) {
    if (!mount) {
      return null;
    }
    return mount.getAttribute("product") ?? null;
  }
  function getProductRecommendationsIntent(mount) {
    if (!mount) {
      return null;
    }
    return mount.getAttribute("intent") ?? null;
  }
  function getMaxProductsCount(mount) {
    if (!mount) {
      return null;
    }
    return mount.getAttribute(MAX_PRODUCTS_COUNT_SELECTOR) ?? null;
  }
  function getFiltersFromSearchParams(searchParams) {
    const filters = [];
    const price = {};
    searchParams.forEach((value, key) => {
      if (!key.startsWith("filter.")) return;
      if (key === "filter.p.availability" || key === "filter.v.availability") {
        filters.push({ available: value === "1" });
        return;
      }
      if (key === "filter.p.price.gte" || key === "filter.v.price.gte") {
        price.min = parseFloat(value);
        return;
      }
      if (key === "filter.p.price.lte" || key === "filter.v.price.lte") {
        price.max = parseFloat(value);
        return;
      }
      if (key === "filter.p.product_type" || key === "filter.v.product_type") {
        filters.push({ productType: value });
        return;
      }
      if (key === "filter.p.vendor" || key === "filter.v.vendor") {
        filters.push({ productVendor: value });
        return;
      }
      if (key === "filter.p.tag" || key === "filter.v.tag") {
        filters.push({ tag: value });
        return;
      }
      const variantOptionMatch = key.match(/^filter\.v\.option\.(.+)$/);
      if (variantOptionMatch) {
        filters.push({
          variantOption: {
            name: variantOptionMatch[1],
            value
          }
        });
        return;
      }
      const productMetafieldMatch = key.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
      if (productMetafieldMatch) {
        filters.push({
          productMetafield: {
            namespace: productMetafieldMatch[1],
            key: productMetafieldMatch[2],
            value
          }
        });
        return;
      }
      const variantMetafieldMatch = key.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
      if (variantMetafieldMatch) {
        filters.push({
          variantMetafield: {
            namespace: variantMetafieldMatch[1],
            key: variantMetafieldMatch[2],
            value
          }
        });
        return;
      }
    });
    if (price.min !== void 0 || price.max !== void 0) {
      filters.push({ price });
    }
    return filters;
  }
  function getPageFromSearchParams(searchParams) {
    const raw = searchParams.get("page");
    const pageNumber = parseInt(raw, 10);
    if (!Number.isFinite(pageNumber) || pageNumber < 1) {
      return 1;
    }
    return pageNumber;
  }
  function getItemSkus(item) {
    if (!item) {
      return [];
    }
    if (item.sku) {
      return [item.sku];
    }
    const variantEdges = item?.variants?.edges;
    if (Array.isArray(variantEdges)) {
      return variantEdges.map(({ node }) => node?.sku).filter(Boolean);
    }
    const variants = item?.variants;
    if (Array.isArray(variants)) {
      return variants.map((variant) => variant?.sku).filter(Boolean);
    }
    return [];
  }
  function isItemMatchingAllowedSkus(item, allowedSkus) {
    if (!allowedSkus?.size) {
      return true;
    }
    return getItemSkus(item).some((sku) => allowedSkus.has(sku));
  }
  function isItemProductTypeGeneric(item) {
    return item?.productType === "Generic" || item?.productType === "GenericMotorbike";
  }
  function filterItemsByAllowedSkus(items, allowedSkus) {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }
    if (!allowedSkus?.size) {
      return items;
    }
    return items.filter((item) => isItemMatchingAllowedSkus(item, allowedSkus) || isItemProductTypeGeneric(item));
  }
  async function renderProductListFragments(htmlFragments, mount, append = false, filters = "") {
    const productContainer = getProductCardsContainer(mount);
    if (!productContainer) {
      return;
    }
    const fragments = Array.isArray(htmlFragments) ? htmlFragments : [];
    if (fragments.length > 0) {
      if (append === "append") {
        productContainer.insertAdjacentHTML("beforeend", fragments.join(""));
      } else if (append === "prepend") {
        productContainer.insertAdjacentHTML("afterbegin", fragments.join(""));
      } else {
        productContainer.innerHTML = fragments.join("");
      }
      if (window.iWish) {
        window.iWish.init();
      }
      return;
    }
    if (!append) {
      await renderEmptyState(productContainer, mount, filters);
    }
  }
  async function renderEmptyState(productContainer, mount, filters = "") {
    if (isMainSearchMount(mount)) {
      productContainer.innerHTML = getNoResultsTemplate();
      return;
    }
    const handle = getProductListMountHandle(mount);
    if (!handle) {
      return;
    }
    const isCollection = productContainer.closest(".collection") !== null;
    const sectionId = isCollection ? COLLECTION_EMPTY_STATE_SECTION_ID : COLLECTION_CAROUSEL_EMPTY_STATE_SECTION_ID;
    const url = `collections/${handle}?section_id=${sectionId}${filters.length > 0 ? `&${filters}` : ""}`;
    const htmlString = await fetch(getUrlWithLanguage(url)).then((response) => response.text());
    const doc = new DOMParser().parseFromString(htmlString, "text/html");
    const emptyRoot = doc.querySelector(".empty-state") ?? doc.querySelector('[id^="shopify-section"]');
    if (emptyRoot) {
      productContainer.innerHTML = emptyRoot.innerHTML;
    }
  }
  function getNoResultsTemplate(attributeName = "data-no-results-template") {
    return document.querySelector(`[${attributeName}]`)?.getAttribute(attributeName) ?? "";
  }
  async function fetchProductCardHtml(handle) {
    const url = `products/${handle}?section_id=${PRODUCT_CARD_SECTION_ID}`;
    const response = await fetch(getUrlWithLanguage(url));
    const htmlString = await response.text();
    const doc = new DOMParser().parseFromString(htmlString, "text/html");
    const section = doc.querySelector(".shopify-section--product-card");
    return section?.innerHTML ?? "";
  }
  function hideProductsCount() {
    document.querySelectorAll(".collection-toolbar__products-count")?.forEach((el) => {
      el.style.display = "none";
    });
  }
  function showProductsCount() {
    document.querySelectorAll(".collection-toolbar__products-count")?.forEach((el) => {
      el.style.display = "block";
    });
  }
  function getUrlWithLanguage(url) {
    url = url.startsWith("/") ? url.slice(1) : url;
    return `${Shopify.routes.root}${url}`;
  }
  function syncPaginationButtonsForMount(mount, { hasNextPage, hasPrevPage }) {
    if (!mount) {
      return;
    }
    const section = mount.closest(".shopify-section");
    if (!section) {
      return;
    }
    const nextButton = section.querySelector('.button[rel="next"]')?.closest(".pagination");
    if (nextButton) {
      if (!hasNextPage) {
        nextButton.style.display = "none";
      } else {
        nextButton.style.display = "block";
      }
    }
    const prevButton = section.querySelector('.button[rel="prev"]')?.closest(".pagination");
    if (prevButton) {
      if (!hasPrevPage) {
        prevButton.style.display = "none";
      } else {
        prevButton.style.display = "block";
      }
    }
  }
  var cachedLoaderMarkup = null;
  function getVehicleSearchLoaderMarkup() {
    if (cachedLoaderMarkup == null) {
      cachedLoaderMarkup = document.getElementById("vehicle-search-loader-icon")?.innerHTML.trim() ?? "";
    }
    return cachedLoaderMarkup;
  }
  function setMountLoading(mount, isLoading, { append = false } = {}) {
    const cardsContainer = getProductCardsContainer(mount);
    if (!mount || !cardsContainer) {
      return;
    }
    if (isLoading) {
      if (mount.querySelector(".vehicle-search-mount__loader")) {
        return;
      }
      const markup = getVehicleSearchLoaderMarkup();
      if (!markup) {
        return;
      }
      if (!append) {
        cardsContainer.replaceChildren();
      }
      if (getComputedStyle(mount).position === "static") {
        mount.style.position = "relative";
      }
      mount.setAttribute("aria-busy", "true");
      if (append) {
        mount.classList.add("vehicle-search-mount--append-loading");
      } else {
        mount.classList.add("vehicle-search-mount--loading");
        const loader = document.createElement("div");
        loader.className = "vehicle-search-mount__loader";
        loader.setAttribute("role", "status");
        loader.innerHTML = markup;
        mount.appendChild(loader);
      }
    } else {
      mount.removeAttribute("aria-busy");
      mount.classList.remove("vehicle-search-mount--loading", "vehicle-search-mount--append-loading");
      mount.style.minHeight = "";
      mount.querySelector(".vehicle-search-mount__loader")?.remove();
    }
  }
  function setPaginationButtonsLoading(button, isLoading) {
    if (!button) {
      return;
    }
    if (isLoading) {
      button.setAttribute("disabled", "");
      button.style.pointerEvents = "none";
      button.classList.add("is-loading");
    } else {
      button.removeAttribute("disabled");
      button.style.pointerEvents = "";
      button.classList.remove("is-loading");
    }
  }
  function restorePaginationButtons(mount) {
    if (!mount) {
      return;
    }
    const section = mount.closest(".shopify-section");
    if (!section) {
      return;
    }
    const prevButton = section.querySelector('.button[rel="prev"]');
    if (prevButton) {
      prevButton.style.display = "none";
    }
    const nextButton = section.querySelector('.button[rel="next"]');
    if (nextButton) {
      nextButton.style.display = "block";
      nextButton.href = nextButton.href.replace(/page=\d+/, "page=2");
    }
  }
  function normalizeSortSelection(sortValue) {
    const graphqlSortKey = SORT_KEYS[sortValue] ?? SORT_KEYS["most-relevant"];
    if (graphqlSortKey.endsWith("_DESCENDING")) {
      return {
        sortKey: graphqlSortKey.replace("_DESCENDING", ""),
        reverse: true
      };
    }
    if (graphqlSortKey.endsWith("_ASCENDING")) {
      return {
        sortKey: graphqlSortKey.replace("_ASCENDING", ""),
        reverse: false
      };
    }
    return {
      sortKey: graphqlSortKey,
      reverse: false
    };
  }
  function getSelectedSortKey() {
    const selectedSortValue = [...document.querySelectorAll("#sort-by-popover .popover__value-option")].find(
      (el) => el.getAttribute("aria-selected") === "true"
    )?.value;
    if (selectedSortValue) {
      return normalizeSortSelection(selectedSortValue);
    }
    if (document.querySelector("[data-main-search]")) {
      return normalizeSortSelection("most-relevant");
    }
    return normalizeSortSelection("collection-default");
  }
  function resetPredictiveSearchMounts() {
    document.querySelectorAll("[data-predictive-search]").forEach((mount) => {
      const form = document.querySelector(`[aria-owns="${mount.id}"]`);
      const input = form?.elements?.q;
      if (input?.value?.trim()) {
        input.dispatchEvent(new Event("input", { bubbles: true }));
      } else {
        mount.querySelector('[slot="results"]')?.replaceChildren();
      }
    });
  }

  // src/js/vehicleSearch/api.js
  function getStorefrontToken() {
    const token = window.Grafite.ResearchVehicle?.graphqlStoreFrontToken;
    return typeof token === "string" && token.trim() !== "" ? token.trim() : null;
  }
  async function storefrontGraphql(query, variables = {}, signal = null) {
    const token = getStorefrontToken();
    if (!token) {
      console.error("Vehicle search: missing Storefront API token in theme settings.");
      return null;
    }
    const response = await fetch(window.Grafite.ResearchVehicle?.graphqlStoreFrontUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token
      },
      body: JSON.stringify({ query, variables }),
      signal
    });
    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status}`);
    }
    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((e) => e.message).join("; "));
    }
    return payload.data;
  }
  async function getOpenSearchProductsSkusFromVehicleSearchStorage() {
    const vehicleSearchStorage = localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY);
    if (!vehicleSearchStorage) {
      return [];
    }
    const { brand, model, year } = JSON.parse(vehicleSearchStorage);
    const url = window.Grafite.ResearchVehicle?.openSearchUrl;
    const auth = window.Grafite.ResearchVehicle?.openSearchAuth;
    if (!url || !auth) {
      console.error("Vehicle search: missing Open Search URL or auth in theme settings.");
      return [];
    }
    const options = {
      method: "POST",
      headers: {
        authorization: auth,
        "content-type": "application/json"
      },
      body: JSON.stringify({
        size: 1e4,
        _source: ["sku", "descrizione"],
        query: {
          nested: {
            path: "compatibilita",
            query: {
              bool: {
                must: [
                  { term: { "compatibilita.brand": brand } },
                  { term: { "compatibilita.modello": model } },
                  { term: { "compatibilita.anni": year } }
                ]
              }
            }
          }
        }
      })
    };
    try {
      const response = await fetch(url, options);
      const data = await response.json();
      return Array.isArray(data.hits.hits) ? new Set(data.hits.hits.map((hit) => hit._source.sku)) : /* @__PURE__ */ new Set();
    } catch (error) {
      console.error(error);
      return /* @__PURE__ */ new Set();
    }
  }
  async function getMockedOpenSearchProductsSkus() {
    try {
      const response = await fetch(window.Grafite.ResearchVehicle?.mockedVehiclesSkusUrl ?? "");
      const data = await response.json();
      return Array.isArray(data?.skus) ? new Set(data.skus) : /* @__PURE__ */ new Set();
    } catch (error) {
      console.error("Failed to load mocked SKUs:", error);
      return /* @__PURE__ */ new Set();
    }
  }
  async function getProductRecommendations(productId, limit, intent) {
    const response = await fetch(window.Shopify.routes.root + `recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`);
    const data = await response.json();
    return data;
  }

  // src/js/vehicleSearch/_vehicle-search.js
  var cachedOpenSearchProductSkus = /* @__PURE__ */ new Set();
  async function loadOpenSearchProductSkus() {
    if (window.Grafite?.ResearchVehicle?.dataSource == "mock") {
      cachedOpenSearchProductSkus = await getMockedOpenSearchProductsSkus();
    } else {
      cachedOpenSearchProductSkus = await getOpenSearchProductsSkusFromVehicleSearchStorage();
    }
    return cachedOpenSearchProductSkus;
  }
  function addEventListeners() {
    document.addEventListener("vehicle-research:selected", async (event) => {
      handleVehicleResearchSelected(event);
      if (localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY)) {
        await loadOpenSearchProductSkus();
        filterProducts(event);
        resetPredictiveSearchMounts();
      } else {
        filterProducts(event, { reset: true });
        resetPredictiveSearchMounts();
        cachedOpenSearchProductSkus = /* @__PURE__ */ new Set();
      }
    });
    let vehicleSearchLoading = false;
    document.addEventListener("vehicle-search:loading:end", async (event) => {
      const mount = event.detail?.mount;
      if (!mount && vehicleSearchLoading) {
        return;
      }
      if (!mount) {
        vehicleSearchLoading = true;
      }
      const button = event.detail?.button;
      if (button) {
        setPaginationButtonsLoading(button, true);
      }
      try {
        await filterProducts(event);
      } finally {
        if (button) {
          setPaginationButtonsLoading(button, false);
        }
        if (!mount) {
          vehicleSearchLoading = false;
        }
      }
    });
    document.addEventListener("vehicle-search:predictive:complete", (event) => {
      handlePredictiveSearchFilter(event);
    });
  }
  async function handlePredictiveSearchFilter(event) {
    const mount = event.detail?.mount;
    if (!mount) {
      return;
    }
    if (cachedOpenSearchProductSkus.size === 0) {
      await loadOpenSearchProductSkus();
    }
    const matchingPredictiveProducts = filterItemsByAllowedSkus(
      event.detail?.predictiveProducts,
      cachedOpenSearchProductSkus
    );
    mount.querySelectorAll("[data-product-sku]")?.forEach((element) => {
      element.setAttribute("hidden", "");
    });
    matchingPredictiveProducts.forEach((product) => {
      const element = mount.querySelector(`[data-product-sku="${product.sku}"]`);
      if (element) {
        element.removeAttribute("hidden");
      }
    });
    if (matchingPredictiveProducts.length === 0) {
      const productsContainer = getProductCardsContainer(mount);
      const emptyState = document.createElement("div");
      emptyState.classList.add("predictive-search-empty-state");
      emptyState.innerHTML = window.Grafite?.ResearchVehicle?.translations?.predictiveSearchEmptyState ?? "";
      productsContainer?.appendChild(emptyState);
    }
  }
  async function filterProducts(event, { reset = false } = {}) {
    if (event.type === "vehicle-search:loading:end" && !localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY)) {
      return;
    }
    const url = event.detail?.url;
    const searchParams = url?.searchParams;
    let filters = null;
    if (searchParams) {
      filters = getFiltersFromSearchParams(searchParams);
    }
    await getFilteredProducts({
      filters,
      searchParams,
      reset,
      append: event.detail?.append ?? false,
      mount: event.detail?.mount
    });
  }
  function initVehicleSearch() {
    addEventListeners();
    if (!localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY)) {
      updateProductCompatibilityBadge();
      return;
    }
  }
  async function getFilteredProducts({
    filters = null,
    searchParams: searchParamsOverride = null,
    reset = false,
    append = false,
    mount: mountOverride = null
  } = {}) {
    if (!localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY) && !reset) {
      return;
    }
    let openSearchProductSkus = /* @__PURE__ */ new Set();
    if (reset) {
      showProductsCount();
      cachedOpenSearchProductSkus = /* @__PURE__ */ new Set();
    } else {
      hideProductsCount();
      openSearchProductSkus = await loadOpenSearchProductSkus();
    }
    const searchParams = searchParamsOverride ?? new URLSearchParams(window.location.search);
    if (!Array.isArray(filters)) {
      filters = getFiltersFromSearchParams(searchParams);
    }
    let page = getPageFromSearchParams(searchParams);
    if (reset) {
      searchParams.delete("page");
      const searchParamsWithoutPage = searchParams.toString();
      const resetUrl = searchParamsWithoutPage.length > 0 ? `${window.location.pathname}?${searchParamsWithoutPage}` : window.location.pathname;
      window.history.replaceState(null, "", resetUrl);
    }
    const mounts = mountOverride ? [mountOverride] : getVehicleSearchMounts();
    const filterQuery = Array.from(searchParams.entries()).filter(([key]) => key.startsWith("filter.")).map(([key, value]) => `${key}=${value}`).join("&");
    const mountConfigs = mounts.map((mount) => {
      const collectionHandle = getProductListMountHandle(mount);
      if (!collectionHandle && !isProductRecommendationsMount(mount) && !isMainSearchMount(mount)) {
        return null;
      }
      const productsPerPage = parseInt(getProductsPerPage(mount) ?? DEFAULT_PRODUCTS_PAGE_SIZE, 10);
      const rawMaxProductsAttr = getMaxProductsCount(mount);
      let finalMaxProductsCount = productsPerPage;
      if (rawMaxProductsAttr != null && String(rawMaxProductsAttr).trim() !== "") {
        finalMaxProductsCount = parseInt(rawMaxProductsAttr, 10);
      }
      return {
        mount,
        collectionHandle,
        finalMaxProductsCount,
        skipMatchingCount: (page - 1) * finalMaxProductsCount
      };
    }).filter(Boolean);
    mountConfigs.forEach(({ mount }) => {
      setMountLoading(mount, true, { append });
    });
    try {
      for (const { mount, collectionHandle, finalMaxProductsCount, skipMatchingCount } of mountConfigs) {
        try {
          const { handles: matchingHandles, hasNextPage } = await getSearchResults({
            mount,
            collectionHandle,
            filters,
            allowedSkus: openSearchProductSkus,
            limit: finalMaxProductsCount,
            skipMatchingCount,
            reset
          });
          await fetchAndRenderProductCards(matchingHandles, mount, append, filterQuery);
          if (reset) {
            restorePaginationButtons(mount);
          } else {
            const hasPrevPage = page > 1;
            syncPaginationButtonsForMount(mount, {
              hasNextPage,
              hasPrevPage
            });
          }
        } finally {
          setMountLoading(mount, false, { append });
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  }
  async function getSearchResults({ mount, collectionHandle, filters, allowedSkus, limit, skipMatchingCount, reset }) {
    if (isProductRecommendationsMount(mount)) {
      return await fetchProductRecommendations(mount, limit, allowedSkus, reset);
    }
    const { sortKey, reverse } = getSelectedSortKey();
    if (isMainSearchMount(mount)) {
      const query = getMainSearchTerms(mount);
      if (!query) {
        return { handles: [], hasNextPage: false };
      }
      return await fetchMatchingHandlesRecursively({
        sourceType: "search",
        query,
        sortKey,
        reverse,
        filters,
        allowedSkus,
        limit,
        skipMatchingCount,
        reset
      });
    }
    return await fetchMatchingHandlesRecursively({
      sourceType: "collection",
      handle: collectionHandle,
      filters,
      allowedSkus,
      limit,
      skipMatchingCount,
      reset,
      sortKey,
      reverse
    });
  }
  function toMatchingHandlesResult(accumulator, pageLimit) {
    return {
      handles: accumulator.slice(0, pageLimit),
      hasNextPage: accumulator.length > pageLimit
    };
  }
  async function fetchMatchingHandlesRecursively({
    sourceType,
    handle,
    query,
    filters,
    allowedSkus,
    limit,
    sortKey,
    reverse = false,
    cursor = null,
    accumulator = [],
    skipMatchingCount = 0,
    pageStartCursor = null,
    pageEndCursor = null,
    reset = false
  }) {
    const lookAheadLimit = limit + 1;
    const collectedCount = accumulator.length;
    const handlesStillNeeded = Math.max(lookAheadLimit - collectedCount, 0);
    if (handlesStillNeeded === 0) {
      return toMatchingHandlesResult(accumulator, limit);
    }
    let edges = [];
    let pageInfo = {};
    if (sourceType === "collection") {
      const data = await storefrontGraphql(GET_PRODUCTS_HANDLE_BY_SKUS, {
        handle,
        filters,
        first: GRAPHQL_COLLECTION_BATCH_SIZE,
        last: null,
        after: cursor,
        before: null,
        sortKey,
        reverse
      });
      const productCollection = data?.collection?.products;
      edges = productCollection?.edges ?? [];
      pageInfo = productCollection?.pageInfo ?? {};
    } else {
      const data = await storefrontGraphql(GET_SEARCH_PRODUCTS_HANDLE_BY_SKUS, {
        query,
        first: GRAPHQL_SEARCH_BATCH_SIZE,
        after: cursor,
        productFilters: filters,
        sortKey,
        reverse
      });
      edges = data?.search?.edges ?? [];
      pageInfo = data?.search?.pageInfo ?? {};
    }
    if (sourceType === "collection" && reset) {
      const forwardPass2 = processForwardEdges({
        edges,
        applySkuFilter: false,
        allowedSkus,
        lookAheadLimit,
        collectedHandles: accumulator
      });
      return toMatchingHandlesResult(forwardPass2.done ? forwardPass2.result : forwardPass2.state.collectedHandles, limit);
    }
    const applySkuFilter = sourceType === "collection" ? true : !reset && allowedSkus.size > 0;
    const forwardPass = processForwardEdges({
      edges,
      applySkuFilter,
      allowedSkus,
      lookAheadLimit,
      collectedHandles: accumulator,
      productsStillToSkip: skipMatchingCount,
      ...sourceType === "collection" && {
        visiblePageStartCursor: pageStartCursor,
        visiblePageEndCursor: pageEndCursor
      }
    });
    if (forwardPass.done) {
      return toMatchingHandlesResult(forwardPass.result, limit);
    }
    const { collectedHandles, productsStillToSkip } = forwardPass.state;
    if (!pageInfo.hasNextPage || !pageInfo.endCursor) {
      return toMatchingHandlesResult(collectedHandles, limit);
    }
    return fetchMatchingHandlesRecursively({
      sourceType,
      handle,
      query,
      filters,
      allowedSkus,
      limit,
      sortKey,
      reverse,
      cursor: pageInfo.endCursor,
      accumulator: collectedHandles,
      skipMatchingCount: productsStillToSkip,
      pageStartCursor,
      pageEndCursor,
      reset
    });
  }
  function processForwardEdges({
    edges,
    applySkuFilter,
    allowedSkus,
    lookAheadLimit,
    collectedHandles,
    productsStillToSkip = 0
  }) {
    let productsStillToSkipMutable = productsStillToSkip;
    let collectedHandlesMutable = collectedHandles;
    for (const edge of edges) {
      if (applySkuFilter && !isItemMatchingAllowedSkus(edge?.node, allowedSkus) && !isItemProductTypeGeneric(edge?.node)) {
        continue;
      }
      if (productsStillToSkipMutable > 0) {
        productsStillToSkipMutable -= 1;
        continue;
      }
      const productHandle = edge?.node?.handle;
      if (!productHandle) {
        continue;
      }
      collectedHandlesMutable = collectedHandlesMutable.concat([productHandle]);
      if (collectedHandlesMutable.length >= lookAheadLimit) {
        return {
          done: true,
          result: collectedHandlesMutable
        };
      }
    }
    return {
      done: false,
      state: {
        collectedHandles: collectedHandlesMutable,
        productsStillToSkip: productsStillToSkipMutable
      }
    };
  }
  async function fetchProductRecommendations(mount, finalMaxProductsCount, openSearchProductSkus, reset) {
    const productRecommendationsProductId = getProductRecommendationsProductId(mount);
    const productRecommendationsIntent = getProductRecommendationsIntent(mount);
    const { products } = await getProductRecommendations(
      productRecommendationsProductId,
      finalMaxProductsCount,
      productRecommendationsIntent
    );
    const applySkuFilter = !reset && openSearchProductSkus.size > 0;
    const matchingProducts = applySkuFilter ? filterItemsByAllowedSkus(products ?? [], openSearchProductSkus) : products ?? [];
    const matchingHandles = matchingProducts.map((product) => product.handle).filter(Boolean);
    return { handles: matchingHandles, hasNextPage: false };
  }
  async function fetchAndRenderProductCards(handles, mount, append = false, filters = "") {
    try {
      const fragments = await Promise.all(
        handles.map((handle) => {
          return fetchProductCardHtml(handle);
        })
      );
      await renderProductListFragments(fragments, mount, append, filters);
    } catch (error) {
      console.error("Error extracting product cards:", error);
    }
  }
  function handleVehicleResearchSelected(event) {
    const { vehicle, vehicleLabel, compatible } = event.detail;
    updateHeaderButton(vehicle, vehicleLabel);
    updateProductCompatibilityBadge(vehicle, compatible);
  }
  function updateProductCompatibilityBadge(vehicle, compatible) {
    const productCompatibilityBadge = document.querySelector("[data-vehicle-compatibility-button]");
    if (!productCompatibilityBadge) {
      return;
    }
    productCompatibilityBadge.querySelectorAll("[data-vehicle-compatibility]").forEach((badge) => {
      badge.setAttribute("hidden", "");
    });
    if (vehicle && compatible) {
      productCompatibilityBadge.querySelector('[data-vehicle-compatibility="compatible"]').removeAttribute("hidden");
    } else if (vehicle && !compatible) {
      productCompatibilityBadge.querySelector('[data-vehicle-compatibility="no_compatible"]').removeAttribute("hidden");
    } else {
      productCompatibilityBadge.querySelector('[data-vehicle-compatibility="no_vehicle"]').removeAttribute("hidden");
    }
  }
  function updateHeaderButton(vehicle, vehicleLabel) {
    const headerButton = document.querySelector(".header__choose-vehicle .header__choose-vehicle--subtitle");
    if (!headerButton) {
      return;
    }
    if (vehicle) {
      headerButton.innerHTML = vehicleLabel;
    } else {
      headerButton.innerHTML = headerButton.dataset.defaultValue;
    }
  }

  // src/js/common.js
  var headerItemClickFn = ({ headerItem, headerItems }) => {
    const level = headerItem.getAttribute("level");
    const sidebar = headerItem.closest("header-sidebar");
    let targetButton = null;
    if (sidebar?.hasAttribute("data-dual-sidebar")) {
      const checkedMenu = sidebar.querySelector(".header-sidebar__menu-radio:checked");
      targetButton = sidebar.querySelector(
        `header-sidebar-collapsible-panel[data-menu-group="${checkedMenu?.value}"].header-sidebar__collapsible-panel-${level} button[data-action="close-panel"]`
      );
    } else {
      targetButton = sidebar?.querySelector(
        `.header-sidebar__collapsible-panel-${level} button[data-action="close-panel"]`
      );
    }
    targetButton?.click();
    headerItems.forEach((item) => {
      item.setAttribute("hidden", true);
      const levelMinus = Number(level) - 1;
      if (item.getAttribute("level") === levelMinus.toString()) {
        item.removeAttribute("hidden");
      }
    });
  };
  var sidebarOpenFn = ({ level, target, headerItems }) => {
    headerItems.forEach((headerItem) => {
      headerItem.setAttribute("hidden", true);
      const itemLevel = headerItem.getAttribute("level");
      const dataTarget = headerItem.getAttribute("data-target");
      if (level === 1 && itemLevel === "1" || level.toString() === itemLevel && dataTarget === target) {
        headerItem.removeAttribute("hidden");
      }
    });
  };
  var sidebarMenuChangeInit = () => {
    const headerItems = document.querySelectorAll(".header-sidebar__menu-header");
    headerItems.forEach((headerItem) => {
      if (!headerItem.getAttribute("level")) {
        return;
      }
      headerItem.addEventListener("click", () => headerItemClickFn({ headerItem, headerItems }));
    });
    document.addEventListener(
      "theme:sidebar:open",
      ({ detail: { level, target } }) => sidebarOpenFn({ level, target, headerItems })
    );
  };
  initVehicleSearch();
  document.addEventListener("DOMContentLoaded", () => {
    sidebarMenuChangeInit();
  });
})();
